// The transcript is its own pane; new text rolls up under the header. We keep it pinned to
// the bottom while turns type. Scrolling up in the pane hands control to the reader; coming
// back to the bottom resumes. The pane is there at every width; without one (no stage css,
// as in the test browser) we nudge the window instead. The words fade as they roll up:
// each message's own opacity from where it sits in the pane (cheap: the compositor's, no
// mask re-rasterising the pane on every typed character).
import { reduced } from "./dom.ts";
import { T } from "./timing.ts";

const state = { follow: true };
export const resumeFollow = () => {
  state.follow = true;
};

/** whether a pane is styled to scroll, asked of its computed style once (the stylesheet does not change under it) */
const scrolls: WeakMap<HTMLElement, boolean> = new WeakMap();
/** the scrolling pane an element lives in, if it scrolls */
const scrollerOf = (el: Element | null) => {
  const sc = el ? (el.closest("[data-transcript]") as HTMLElement | null) : null;
  if (!sc) return null;
  let styled = scrolls.get(sc);
  if (styled === undefined) {
    styled = getComputedStyle(sc).overflowY !== "visible";
    scrolls.set(sc, styled);
  }
  return styled && sc.scrollHeight > sc.clientHeight + 2 ? sc : null;
};

/** the opacity last written on each row, so a row is written only when its fade changes */
const written: WeakMap<HTMLElement, number> = new WeakMap();

/**
 * The fade by row: a message whole from the pane's bottom up to its middle, fading over the quarter above, gone above
 * that; a dimmed one (typewriter.js focusMsg) at its dimness within that. Every row read first and then written, so
 * one layout serves them all (a write between reads forces a style recalc per row), and a row written only when
 * its fade has changed.
 */
/** a finished bout on its own page (/bout/<id>), read from the top rather than followed at the bottom (#628) */
export const archived = (sc: Element | null) => !!sc?.closest(".bout-page article.bout[data-done]");

export const fadeRows = (sc: HTMLElement | null) => {
  if (!sc) return;
  const r = sc.getBoundingClientRect(),
    h = r.height;
  if (!h) return;
  const rows = [...sc.children] as HTMLElement[];
  const reading = archived(sc); // a finished bout's own page: nothing fades at the top, every turn is there to read (#628)
  const fades = rows.map((li) => {
    if (reading) return li.classList.contains("dimmed") ? 0.45 : 1;
    const b = li.getBoundingClientRect(),
      u = ((b.top + b.bottom) / 2 - r.top) / h; // 0 the top .. 1 the bottom
    const fade = u >= 0.25 ? 1 : u <= 0 ? 0 : u / 0.25;
    return fade * (li.classList.contains("dimmed") ? 0.45 : 1);
  });
  rows.forEach((li, i) => {
    if (written.get(li) !== fades[i]) {
      written.set(li, fades[i]);
      li.style.opacity = String(fades[i]);
    }
  });
};

/** the panes with a fade asked for this frame */
const asked: Set<HTMLElement> = new Set();
/** the fade at the next frame, once however often it is asked for (a smooth scroll fires a scroll event every frame) */
const fadeSoon = (sc: HTMLElement | null) => {
  if (!sc || asked.has(sc)) return;
  if (!asked.size)
    requestAnimationFrame(() => {
      for (const p of asked) fadeRows(p);
      asked.clear();
    });
  asked.add(sc);
};

export const keepInView = (el: HTMLElement | null) => {
  if (!state.follow || !el) return;
  const sc = scrollerOf(el);
  // already at the bottom: no scroll to start (a smooth scroll fires a scroll event, and a fade, every frame it runs)
  if (sc) {
    if (sc.scrollHeight - sc.scrollTop - sc.clientHeight > 1)
      sc.scrollTo({ top: sc.scrollHeight, behavior: reduced ? "auto" : "smooth" });
    return;
  }
  const r = el.getBoundingClientRect();
  const limit = window.innerHeight - T.FOLLOW_MARGIN;
  if (r.bottom > limit)
    window.scrollBy({ top: r.bottom - limit + T.FOLLOW_STEP, behavior: reduced ? "auto" : "smooth" });
};

export const wireFollow = (sc: HTMLElement | null) => {
  if (!sc || sc.dataset.wired) return;
  sc.dataset.wired = "1";
  sc.addEventListener(
    "wheel",
    (e) => {
      if (e.deltaY < 0) state.follow = false;
    },
    { passive: true },
  );
  // a finger dragging down (reading older turns) hands over; a tap or an upward flick does not,
  // else a touch on a pane already at its bottom would stop the follow with nothing to restart it
  let y0 = 0;
  sc.addEventListener(
    "touchstart",
    (e) => {
      y0 = e.touches[0]?.clientY ?? 0;
    },
    { passive: true },
  );
  sc.addEventListener(
    "touchmove",
    (e) => {
      const y = e.touches[0]?.clientY ?? y0;
      if (y - y0 > T.FOLLOW_DRAG) state.follow = false;
    },
    { passive: true },
  );
  sc.addEventListener(
    "scroll",
    () => {
      if (sc.scrollHeight - sc.scrollTop - sc.clientHeight < T.FOLLOW_BOTTOM) state.follow = true;
      fadeSoon(sc);
    },
    { passive: true },
  );
  fadeRows(sc);
};

window.addEventListener("keydown", (e) => {
  if (["ArrowUp", "PageUp", "Home"].includes(e.key)) state.follow = false;
  if (e.key === "End") state.follow = true;
});

/** while an element is typing, nudge every so often */
export const followWhile = (el: HTMLElement, isDone: () => boolean) => {
  const sc = el.closest("[data-transcript]") as HTMLElement | null;
  wireFollow(sc);
  // a hidden tab has nothing to follow; the typewriter catches up on its return, and the next nudge with it
  const t = setInterval(() => {
    if (isDone()) {
      clearInterval(t);
      fadeSoon(sc);
      return;
    }
    if (document.hidden) return;
    keepInView(el);
    fadeSoon(sc);
  }, T.FOLLOW_NUDGE);
  keepInView(el);
};
